<template>
  <div class="w-full">
    <Card>
      <template #content>
        <div class="flex flex-row justify-between">
          <div
            v-if="props.user.status == companyGivingUserProfileStatus.ACTIVE"
            data-test="user-active"
          >
            <p class="text-[12px]">Name</p>
            <p class="font-medium">{{ props.user.donor_account.firstName }} {{ props.user.donor_account.lastName }}</p>
          </div>
          <div
            v-else
            data-test="user-pending"
          >
            <p class="text-[12px]">Status</p>
            <Tag
              rounded
              class="status-badge"
            >
              PENDING
            </Tag>
          </div>
          <div>
            <ActionPicker
              :actions="dynamicMenuItems"
              style="width: 20px"
              class="whitespace-nowrap"
              :data-test="`CompanyUser${props.user.companyGivingUsers_profiles_id}`"
              @removeUser="removeUser"
              @resendInvite="resendInvite"
            >
              <template #trigger>
                <span
                  class="cursor-pointer text-[25px] absolute top-[-18px]"
                  data-test="action-picker-trigger"
                >
                  &hellip;
                </span>
              </template>
            </ActionPicker>
          </div>
        </div>
        <div class="flex flex-row mt-4">
          <div class="w-full">
            <p class="text-[12px]">Email</p>
            <p
              class="font-medium text-ellipsis overflow-hidden"
              data-test="user-email"
            >
              {{ props.user.donor_account?.email ?? props.user.invitedEmail ?? '&mdash;' }}
            </p>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import { useToastStore } from '@/stores/ToastStore.js';
import { companyGivingUserProfileStatus } from '@/helpers/enum.js';
import ActionPicker from '@/components/ActionPicker.vue';
import Email from '@/assets/Email.svg?raw';
import TrashcanBlack from '@/assets/TrashcanBlack.svg?raw';

const props = defineProps({
  user: Object,
});

const companyAdminStore = useCompanyAdminStore();

const dynamicMenuItems = computed(() => {
  let actionPickerItems = [];

  if (props.user.status == companyGivingUserProfileStatus.PENDING) {
    actionPickerItems.push({
      text: 'Resend Invite',
      emit: 'resendInvite',
      disabled: false,
      disabled_text: null,
      type: 'primary',
      icon: Email,
    });
  }
  actionPickerItems.push({
    text: 'Remove User',
    emit: 'removeUser',
    disabled: false,
    disabled_text: null,
    type: 'primary',
    icon: TrashcanBlack,
  });

  return JSON.stringify(actionPickerItems);
});

const removeUser = async () => {
  try {
    await companyAdminStore.removeUser(props.user.companyGivingUsers_profiles_id);
    useToastStore().add({
      severity: 'success',
      summary: 'User Removed from Company',
      detail: 'The user has been removed from the company.',
      life: 3000,
    });
  } catch (e) {
    console.error(e);
    useToastStore().add({
      severity: 'error',
      summary: 'Error',
      detail: e.response
        ? e.response.data.error
        : 'An error occurred while removing a new user. Please try again later.',
      life: 3000,
    });
  }
};

const resendInvite = async () => {
  try {
    await companyAdminStore.resendUserInvite(props.user.companyGivingUsers_profiles_id);
    useToastStore().add({
      severity: 'success',
      summary: 'Invite Resent',
      detail: 'The invite has been resent to the user.',
      life: 3000,
    });
  } catch (e) {
    console.error(e);
    useToastStore().add({
      severity: 'error',
      summary: 'Error',
      detail: e.response ? e.response.data.error : 'An error occurred while adding a new user. Please try again later.',
      life: 3000,
    });
  }
};
</script>

<style scoped>
.status-badge {
  color: black;
  background-color: rgb(252 234 179 / var(--tw-bg-opacity, 1));
  font-weight: 500;
  padding-left: 2rem;
  padding-right: 2rem;
}
</style>
